import { inject, Injectable } from '@angular/core';
import { AuthConfig, OAuthService, ReceivedTokens } from 'angular-oauth2-oidc';
import { Subject } from 'rxjs';
import { CommonStore, ShoppingInfoStore } from '../stores';
import { MessagingStore } from '../stores/amm-message.store';
import { AuthService } from './auth.service';
import { RbacService } from './rbac.service';
import { SseService } from './sse.service';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';
import { APPCONSTANT } from '../constants';

export interface UserInfo {
  info: {
    sub: string;
    email: string;
    given_name: string;
    family_name: string;
    picture: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class GoogleApiService {
  gmail = 'https://gmail.googleapis.com';
  token: string = '';
  userProfileSubject = new Subject<UserInfo>();
  googleRoleError = new Subject<string>();

  authCodeFlowConfig: AuthConfig = {
    // Url of the Identity Provider
    issuer: 'https://accounts.google.com',

    // strict discovery document disallows urls which not start with issuers url
    strictDiscoveryDocumentValidation: false,

    // URL of the SPA to redirect the user to after login
    redirectUri: '',
    logoutUrl: '',
    //TODO: these configurations should be stored in the secret file rather then directly
    //TODO: directly store in the source file
    clientId: environment.googleSigninClientId,

    scope: 'openid profile email',
    showDebugInformation: true,
  };
  private _storageService = inject(StorageService);
  private shoppingInfoStore = inject(ShoppingInfoStore);
  private messageStore = inject(MessagingStore);
  private sseService = inject(SseService);
  private authGoogleService = inject(AuthService);
  private commonStore = inject(CommonStore);
  constructor(
    private readonly oAuthService: OAuthService,
    private _rbacService: RbacService
  ) {}

  public initGoogleAPI() {
    this.authCodeFlowConfig.redirectUri = window?.location.origin;
    this.authCodeFlowConfig.logoutUrl = window?.location.origin;

    // confiure oauth2 service
    this.oAuthService.configure(this.authCodeFlowConfig);
    // manually configure a logout url, because googles discovery document does not provide it
    this.oAuthService.logoutUrl = 'https://www.google.com/accounts/Logout';

    this.oAuthService.loadDiscoveryDocumentAndTryLogin({
      onTokenReceived: (receivedTokens: ReceivedTokens) => {
        this._rbacService
          .googleLogin(receivedTokens.idToken)
          .subscribe((info) => {
            if ((!info || info?.error) && info?.code.includes('403')) {
              this.authGoogleService.googleRoleErr$.next('role==seller');
              return;
            }

            if (this.oAuthService.hasValidAccessToken()) {
              this.oAuthService.loadUserProfile().then((userProfile) => {
                if ((userProfile as UserInfo).info?.picture.length > 0) {
                  this._storageService.setLocalItem(
                    'avatar',
                    (userProfile as UserInfo).info.picture
                  );
                }
              });
            }
            this._storageService.setLocalItem(
              APPCONSTANT.BUYER_INFO,
              JSON.stringify(info)
            );
            this.shoppingInfoStore.loadAllFavorites();
            this.shoppingInfoStore.loadShoppingCarts();
            const jwt = this._storageService.getJWT();
            if (!this.sseService.isConnected && jwt) {
              this.sseService.connectToSSE(jwt);
            }
            this.messageStore.loadConversations();
            this.commonStore.loadBuyerInfo();
          });
      },
      preventClearHashAfterLogin: false,
    });
  }

  signin() {
    this.oAuthService.initCodeFlow();
  }

  signout() {
    //  this is to logout google from your borwser, should never used
    this.oAuthService.revokeTokenAndLogout();
    this.oAuthService.logOut();
    this._storageService.removeLocalItem('avatar');
  }

  isLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }
}
